import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ConfigurationSaveStatusDispatch from "../../../context/configurationSaveStatusContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import useTabs from "../../../hooks/useTabs";
import useI18n from "../../../hooks/useTranslations";
import { BorderConfiguration, BorderConfigurationData } from "../../../http/border/types/configurations";
import { useBorderSettingsApi } from "../../../http/border/useBorderSettings";
import { UpdateConfigurationBody } from "../../../http/types/configurations";
import { useConfigurationsApi } from "../../../http/useConfigurations";
import { getUILanguages } from "../../../types";
import ErrorText from "../../ui/ErrorText";
import FlexContent from "../../ui/FlexContent";
import Tabs, { Tab } from "../../ui/Tabs";
import BorderAccessories, { BorderAccessoriesFormBody } from "./BorderAccessories";
import BorderCalculationPanel from "./BorderCalculationPanel";
import BorderGeneralData, { BorderGeneralDataFormBody } from "./BorderGeneralData";
import BorderPerimeterStep from "./BorderPerimeterStep";
import BorderUprightSelection, {
  BorderUprightSelectionFormBody,
  resolveBorderRail,
  resolveBorderToeBoard,
} from "./BorderUprightSelection";

export interface UpdateBorderConfigurationFormBody {
  generalData: BorderGeneralDataFormBody;
  uprightSelection: BorderUprightSelectionFormBody;
}

interface BorderProps {
  configuration: BorderConfiguration;
  isAdmin?: boolean;
}

const getInitialAccessoriesValues = (configuration: BorderConfiguration): BorderAccessoriesFormBody => ({
  wallTerminalQuantity: configuration.data?.wallTerminalQuantity ?? 0,
  nonWallTerminalQuantity: configuration.data?.nonWallTerminalQuantity ?? 0,
  waterproofKit: configuration.data?.waterproofKit ?? false,
  gateQuantity: configuration.data?.gateQuantity ?? null,
});

const Border: FC<BorderProps> = ({ configuration, isAdmin }: BorderProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const setSaveStatus = useContext(ConfigurationSaveStatusDispatch);
  const configurationsApi = useConfigurationsApi();
  const { configurationId } = useParams<{ configurationId: string }>();
  const languages = getUILanguages(i18n.translation);
  const borderSettingsApi = useBorderSettingsApi();

  const aborter = useRef(new AbortController());
  const { isLoading, data: settings } = borderSettingsApi.useBorderSettings(aborter.current.signal, configurationId);

  const [defaultValues, setDefaultValues] = useState<UpdateBorderConfigurationFormBody>();
  const [accessoriesValues, setAccessoriesValues] = useState<BorderAccessoriesFormBody>(() =>
    getInitialAccessoriesValues(configuration),
  );
  const effectiveConfigurationId = configurationId ?? configuration.id;

  const fallbackSystem = settings?.systems?.[0] ?? null;
  const systemNormative =
    settings?.systemNormatives?.find((x) => x.id === configuration.data?.systemNormativeId) ??
    settings?.systemNormatives?.find((x) => x.system.id === fallbackSystem?.id) ??
    settings?.systemNormatives?.[0] ??
    null;
  const system = systemNormative?.system ?? fallbackSystem;
  const normative = systemNormative?.normative ?? settings?.normatives?.[0] ?? null;

  const uprightMapping =
    settings?.uprightMappings?.find(
      (x) =>
        x.systemNormative.system.id === system?.id &&
        x.systemNormative.normative.id === normative?.id &&
        x.guardrailComposition.id === configuration.data?.guardrailCompositionId &&
        x.upright.item.id === configuration.data?.uprightId,
    ) ??
    settings?.uprightMappings?.find(
      (x) =>
        x.systemNormative.system.id === system?.id &&
        x.systemNormative.normative.id === normative?.id &&
        x.guardrailComposition.id === configuration.data?.guardrailCompositionId,
    ) ??
    settings?.uprightMappings?.find(
      (x) => x.systemNormative.system.id === system?.id && x.systemNormative.normative.id === normative?.id,
    );

  const guardrailComposition =
    uprightMapping?.guardrailComposition ??
    settings?.guardrailCompositions?.find((x) => x.id === configuration.data?.guardrailCompositionId) ??
    settings?.guardrailCompositions?.[0] ??
    null;

  const upright =
    uprightMapping?.upright ??
    settings?.uprights?.find((x) => x.item.id === configuration.data?.uprightId) ??
    settings?.uprights?.[0] ??
    null;

  const ballast = settings?.ballasts?.find((x) => x.item.id === configuration.data?.ballastId) ?? null;
  const substructure = settings?.substructures?.find((x) => x.id === configuration.data?.substructureId) ?? null;
  const base = settings?.bases?.find((x) => x.item.id === configuration.data?.baseId) ?? null;
  const baseCorrugationSpan = configuration.data?.baseCorrugationSpan ?? base?.corrugationSpan ?? null;
  const matchingBaseIdsForCorrugationSpan = new Set(
    (settings?.uprightSpanMappings ?? [])
      .filter(
        (x) =>
          x.systemNormative.system.id === system?.id &&
          x.systemNormative.normative.id === normative?.id &&
          x.upright.item.id === upright?.item.id &&
          (ballast ? x.ballast?.item.id === ballast.item.id : !x.ballast) &&
          (substructure ? x.substructure?.id === substructure.id : !x.substructure) &&
          x.base?.corrugationSpan === baseCorrugationSpan,
      )
      .map((x) => x.base?.item.id)
      .filter((baseId): baseId is string => !!baseId),
  );
  const resolvedBase = baseCorrugationSpan != null && matchingBaseIdsForCorrugationSpan.size > 1 ? null : base;
  const orientationValuesForSelection = new Set(
    (settings?.uprightSpanMappings ?? [])
      .filter(
        (x) =>
          x.systemNormative.system.id === system?.id &&
          x.systemNormative.normative.id === normative?.id &&
          x.upright.item.id === upright?.item.id &&
          (ballast ? x.ballast?.item.id === ballast.item.id : !x.ballast) &&
          (substructure ? x.substructure?.id === substructure.id : !x.substructure),
      )
      .map((x) => x.base?.baseOrientation)
      .filter((orientation): orientation is string => !!orientation),
  );
  const needsOrientation = orientationValuesForSelection.size > 1;
  const accessoriesSystem = defaultValues?.uprightSelection.system ?? system;
  const accessoriesBase = defaultValues?.uprightSelection.base ?? resolvedBase;
  const canShowWaterproofKit = !!accessoriesSystem?.isWaterproofKitVisible && !!accessoriesBase?.waterproofKit;
  const accessoriesDefaultValues = accessoriesValues;

  const spanMapping = settings?.uprightSpanMappings?.find((x) => {
    const baseMatches = resolvedBase
      ? x.base?.item.id === resolvedBase.item.id
      : baseCorrugationSpan != null
        ? x.base?.corrugationSpan === baseCorrugationSpan
        : !x.base;

    return (
      x.systemNormative.system.id === system?.id &&
      x.systemNormative.normative.id === normative?.id &&
      x.upright.item.id === upright?.item.id &&
      (ballast ? x.ballast?.item.id === ballast.item.id : !x.ballast) &&
      (substructure ? x.substructure?.id === substructure.id : !x.substructure) &&
      baseMatches
    );
  });
  const selectedSpan = spanMapping?.span ?? configuration.data?.span ?? null;
  const rail = resolveBorderRail(
    settings?.rails,
    upright?.upperHoleDiameter,
    normative?.reinforcedRailRequired,
    configuration.data?.railId,
  );
  const intermediateRail = guardrailComposition?.isIntermediateBarRequired
    ? resolveBorderRail(
        settings?.rails,
        upright?.lowerHoleDiameter,
        normative?.reinforcedRailRequired,
        configuration.data?.intermediateRailId,
      )
    : null;
  const toeBoard = resolveBorderToeBoard(
    settings?.toeBoards,
    guardrailComposition?.isToeBoardRequired,
    configuration.data?.toeBoardId,
  );

  const fallbackBorderData: BorderConfigurationData | null =
    systemNormative && guardrailComposition && upright
      ? {
          systemNormativeId: systemNormative.id,
          guardrailCompositionId: guardrailComposition.id,
          uprightId: upright.item.id,
          ballastId: ballast?.item.id ?? null,
          substructureId: substructure?.id ?? null,
          baseId: resolvedBase?.item.id ?? null,
          baseCorrugationSpan: baseCorrugationSpan ?? null,
          span: selectedSpan ?? 0,
          railId: rail?.item.id ?? null,
          intermediateRailId: intermediateRail?.item.id ?? null,
          toeBoardId: toeBoard?.item.id ?? null,
          terminalQuantity: 0,
          wallTerminalQuantity: 0,
          nonWallTerminalQuantity: 0,
          gateQuantity: 0,
          waterproofKit: false,
          inputMethod: "manual",
          isClosedPerimeter: false,
          hasCurb: false,
          curbHeight: null,
          curbDepth: null,
          segments: [],
        }
      : null;

  const borderData = configuration.data ?? fallbackBorderData;

  useEffect(() => {
    if (settings) {
      const generalData = {
        name: configuration.name,
        language: languages.find((x) => x.value === configuration.language) ?? languages[0]!,
        description: configuration.description ?? null,
      };

      setDefaultValues({
        generalData,
        uprightSelection: {
          system,
          normative,
          guardrailComposition,
          upright,
          ballastType: ballast?.ballastType ?? null,
          ballast,
          substructure,
          base: resolvedBase,
          baseCorrugationSpan,
          span: selectedSpan,
          rail,
          intermediateRail,
          hasCurb: configuration.data?.hasCurb ?? false,
          curbHeight: configuration.data?.curbHeight ?? null,
          curbDepth: configuration.data?.curbDepth ?? null,
        },
      });
    }
  }, [settings]);

  useEffect(() => {
    setAccessoriesValues(getInitialAccessoriesValues(configuration));
  }, [
    configuration.id,
    configuration.data?.wallTerminalQuantity,
    configuration.data?.nonWallTerminalQuantity,
    configuration.data?.gateQuantity,
    configuration.data?.waterproofKit,
  ]);

  useEffect(() => {
    if (!canShowWaterproofKit) {
      setAccessoriesValues((current) => (current.waterproofKit ? { ...current, waterproofKit: false } : current));
    }
  }, [canShowWaterproofKit]);

  const updateAccessories = async (accessories: BorderAccessoriesFormBody) => {
    if (!borderData) {
      dispatch(showErrorNotification(i18n, undefined));
      setSaveStatus("draft");
      return;
    }

    const border: BorderConfigurationData = {
      ...borderData,
      terminalQuantity: accessories.wallTerminalQuantity + accessories.nonWallTerminalQuantity,
      wallTerminalQuantity: accessories.wallTerminalQuantity,
      nonWallTerminalQuantity: accessories.nonWallTerminalQuantity,
      gateQuantity: accessories.gateQuantity ?? 0,
      waterproofKit: accessories.waterproofKit,
    };
    const body: UpdateConfigurationBody = {
      name: configuration.name,
      description: configuration.description ?? null,
      language: configuration.language,
      border,
    };
    const optimisticData: BorderConfiguration = {
      ...configuration,
      ...body,
      description: configuration.description,
      data: border,
    };

    try {
      await configurationsApi.updateConfiguration(
        configuration.project?.id,
        effectiveConfigurationId,
        body,
        optimisticData,
      );
      setAccessoriesValues(accessories);
      setSaveStatus("saved");
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
      setSaveStatus("draft");
    }
  };

  const submit = async (formBody: UpdateBorderConfigurationFormBody): Promise<void> => {
    if (!borderData) {
      dispatch(showErrorNotification(i18n, new Error("Border settings unavailable")));
      setSaveStatus("draft");
      return;
    }

    const selectedSystemNormative = settings?.systemNormatives?.find(
      (x) =>
        x.system.id === formBody.uprightSelection.system?.id &&
        x.normative.id === formBody.uprightSelection.normative?.id,
    );
    const selectedMatchingBaseIdsForCorrugationSpan = new Set(
      (settings?.uprightSpanMappings ?? [])
        .filter(
          (x) =>
            x.systemNormative.system.id === formBody.uprightSelection.system?.id &&
            x.systemNormative.normative.id === formBody.uprightSelection.normative?.id &&
            x.upright.item.id === formBody.uprightSelection.upright?.item.id &&
            (formBody.uprightSelection.ballast
              ? x.ballast?.item.id === formBody.uprightSelection.ballast.item.id
              : !x.ballast) &&
            (formBody.uprightSelection.substructure
              ? x.substructure?.id === formBody.uprightSelection.substructure.id
              : !x.substructure) &&
            x.base?.corrugationSpan === formBody.uprightSelection.baseCorrugationSpan,
        )
        .map((x) => x.base?.item.id)
        .filter((baseId): baseId is string => !!baseId),
    );
    const selectedBase =
      formBody.uprightSelection.baseCorrugationSpan != null && selectedMatchingBaseIdsForCorrugationSpan.size > 1
        ? null
        : formBody.uprightSelection.base;
    const selectedSpanMapping = settings?.uprightSpanMappings?.find((x) => {
      const baseMatches = selectedBase
        ? x.base?.item.id === selectedBase.item.id
        : formBody.uprightSelection.baseCorrugationSpan != null
          ? x.base?.corrugationSpan === formBody.uprightSelection.baseCorrugationSpan
          : !x.base;

      return (
        x.systemNormative.system.id === formBody.uprightSelection.system?.id &&
        x.systemNormative.normative.id === formBody.uprightSelection.normative?.id &&
        x.upright.item.id === formBody.uprightSelection.upright?.item.id &&
        (formBody.uprightSelection.ballast
          ? x.ballast?.item.id === formBody.uprightSelection.ballast.item.id
          : !x.ballast) &&
        (formBody.uprightSelection.substructure
          ? x.substructure?.id === formBody.uprightSelection.substructure.id
          : !x.substructure) &&
        baseMatches
      );
    });
    const nextSpan = selectedSpanMapping?.span ?? formBody.uprightSelection.span ?? null;
    const selectedRail = resolveBorderRail(
      settings?.rails,
      formBody.uprightSelection.upright?.upperHoleDiameter,
      formBody.uprightSelection.normative?.reinforcedRailRequired,
      formBody.uprightSelection.rail?.item.id ?? borderData.railId,
    );
    const selectedIntermediateRail = formBody.uprightSelection.guardrailComposition?.isIntermediateBarRequired
      ? resolveBorderRail(
          settings?.rails,
          formBody.uprightSelection.upright?.lowerHoleDiameter,
          formBody.uprightSelection.normative?.reinforcedRailRequired,
          formBody.uprightSelection.intermediateRail?.item.id ?? borderData.intermediateRailId,
        )
      : null;
    const selectedToeBoard = resolveBorderToeBoard(
      settings?.toeBoards,
      formBody.uprightSelection.guardrailComposition?.isToeBoardRequired,
      borderData.toeBoardId,
    );
    const selectedUprightMapping = settings?.uprightMappings?.find(
      (x) =>
        x.systemNormative.system.id === formBody.uprightSelection.system?.id &&
        x.systemNormative.normative.id === formBody.uprightSelection.normative?.id &&
        x.guardrailComposition.id === formBody.uprightSelection.guardrailComposition?.id &&
        x.upright.item.id === formBody.uprightSelection.upright?.item.id,
    );
    const canSubmitCurb =
      formBody.uprightSelection.hasCurb &&
      !formBody.uprightSelection.guardrailComposition?.isToeBoardRequired &&
      selectedUprightMapping?.minCurbHeight != null;
    const border: BorderConfigurationData = {
      ...borderData,
      systemNormativeId: selectedSystemNormative?.id!,
      guardrailCompositionId: formBody.uprightSelection.guardrailComposition?.id!,
      uprightId: formBody.uprightSelection.upright?.item.id!,
      ballastId: formBody.uprightSelection.ballast?.item.id ?? null,
      nrBallastTerminal: selectedSpanMapping?.nrBallastTerminal ?? null,
      nrBallastInt: selectedSpanMapping?.nrBallastInt ?? null,
      substructureId: formBody.uprightSelection.substructure?.id ?? null,
      baseId: selectedBase?.item.id ?? null,
      baseCorrugationSpan: formBody.uprightSelection.baseCorrugationSpan ?? null,
      span: nextSpan ?? 0,
      railId: selectedRail?.item.id ?? null,
      intermediateRailId: selectedIntermediateRail?.item.id ?? null,
      toeBoardId: selectedToeBoard?.item.id ?? null,
      hasCurb: canSubmitCurb,
      curbHeight: canSubmitCurb ? (formBody.uprightSelection.curbHeight ?? null) : null,
      curbDepth:
        canSubmitCurb && formBody.uprightSelection.system?.isCurbDepthVisible
          ? (formBody.uprightSelection.curbDepth ?? null)
          : null,
    };
    const body: UpdateConfigurationBody = {
      name: formBody.generalData.name,
      description: formBody.generalData.description ?? null,
      language: formBody.generalData.language?.value!,
      border,
    };

    const optimisticData: BorderConfiguration = {
      ...configuration,
      ...body,
      description: formBody.generalData.description ?? undefined,
      data: border,
    };

    try {
      const res = await configurationsApi.updateConfiguration(
        configuration.project?.id,
        configuration.id,
        body,
        optimisticData,
      );

      setDefaultValues({
        generalData: { ...formBody.generalData },
        uprightSelection: {
          ...formBody.uprightSelection,
          span: border.span,
          rail: selectedRail,
          intermediateRail: selectedIntermediateRail,
        },
      });
      isAdmin && dispatch(showSuccessNotification(i18n.translation));
      setSaveStatus("saved");
      return res;
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
      setSaveStatus("draft");
      return Promise.reject();
    }
  };

  const tabs: Tab[] = [
    {
      title: i18n.translation.common.generalData,
    },
    {
      title: i18n.translation.uprights.singular,
      disabled: !defaultValues?.uprightSelection.system || !defaultValues?.uprightSelection.normative,
    },
    {
      title: i18n.translation.common.perimeter,
      disabled: !defaultValues,
    },
    {
      title: "Accessories",
      disabled: !defaultValues,
    },
    {
      title: i18n.translation.items.list,
      disabled: !defaultValues,
    },
  ];

  const controller = useTabs(tabs.length);

  return (
    <Tabs
      controller={controller}
      tabs={tabs}
      contents={[
        <FlexContent padding>
          <BorderGeneralData
            isAdmin={isAdmin}
            defaultValues={defaultValues?.generalData}
            languages={languages}
            isLoading={isLoading}
            canEdit={configuration.canEdit}
            next={controller.next}
            onSubmit={(generalData) => {
              if (defaultValues) {
                const body: UpdateBorderConfigurationFormBody = {
                  ...defaultValues,
                  generalData,
                };

                return submit(body);
              }
              return undefined;
            }}
          />
        </FlexContent>,
        <FlexContent padding direction="row">
          <BorderUprightSelection
            isAdmin={isAdmin}
            defaultValues={defaultValues?.uprightSelection}
            settings={settings}
            isLoading={isLoading}
            canEdit={configuration.canEdit}
            next={controller.next}
            back={controller.previous}
            onSubmit={(uprightSelection) => {
              if (defaultValues) {
                const body: UpdateBorderConfigurationFormBody = {
                  ...defaultValues,
                  uprightSelection,
                };

                return submit(body);
              }
              return undefined;
            }}
          />
        </FlexContent>,
        borderData ? (
          <FlexContent padding direction="row">
            <BorderPerimeterStep
              configuration={configuration}
              borderData={borderData}
              configurationId={effectiveConfigurationId}
              canEdit={configuration.canEdit}
              isAdmin={isAdmin}
              disabled={isLoading || !defaultValues}
              needsOrientation={needsOrientation}
              isPerimeterSplitRequired={!!system?.isPerimeterSplitRequired}
              isCornerOrientationRequired={!!system?.isCornerOrientationRequired}
              uprightTypeCode={upright?.type.code ?? null}
              next={controller.next}
              back={controller.previous}
            />
          </FlexContent>
        ) : (
          <FlexContent padding direction="row">
            <ErrorText text={i18n.translation.common.genericError} />
          </FlexContent>
        ),
        <FlexContent padding direction="row">
          <BorderAccessories
            isAdmin={isAdmin}
            defaultValues={accessoriesDefaultValues}
            canEdit={configuration.canEdit}
            isLoading={isLoading}
            next={controller.next}
            back={controller.previous}
            showWaterproofKit={canShowWaterproofKit}
            onSubmit={updateAccessories}
          />
        </FlexContent>,
        <BorderCalculationPanel
          configurationId={effectiveConfigurationId}
          disabled={!defaultValues}
          isAdmin={isAdmin}
          back={controller.previous}
        />,
      ]}
    />
  );
};

export default Border;
